import { useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useOrdersSearch, useOrderStatistics } from "@/api/hooks";
import type { Order, OrderStatus } from "@/api/types";

const STATUS_LABELS: Record<OrderStatus, string> = {
  draft: "Brouillon",
  pending: "En attente",
  sent: "Envoyée",
  partially_received: "Partiellement reçue",
  received: "Reçue",
  cancelled: "Annulée",
  closed: "Clôturée",
};

const STATUS_COLORS: Record<OrderStatus, string> = {
  draft: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  sent: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  partially_received: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  received: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  closed: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
};

export function OrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") as OrderStatus | null;

  const { data: orders, isLoading, error } = useOrdersSearch(statusFilter || undefined);
  const { data: statistics } = useOrderStatistics();

  const handleStatusFilter = useCallback(
    (status: OrderStatus | null) => {
      const params = new URLSearchParams(searchParams);
      if (status) {
        params.set("status", status);
      } else {
        params.delete("status");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const formatCurrency = (cents: string | number) => {
    const value = typeof cents === "string" ? parseInt(cents, 10) : cents;
    return (value / 100).toFixed(2) + " €";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Commandes</h1>
        <Link
          to="/acquisition/orders/new"
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
        >
          <span>+</span>
          Nouvelle commande
        </Link>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-sm text-gray-500 dark:text-gray-400">En attente d'approbation</p>
            <p className="mt-1 text-2xl font-semibold text-yellow-600 dark:text-yellow-400">
              {statistics.pending_approval}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-sm text-gray-500 dark:text-gray-400">En attente de livraison</p>
            <p className="mt-1 text-2xl font-semibold text-blue-600 dark:text-blue-400">
              {statistics.awaiting_delivery}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total commandes</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
              {Object.values(statistics.statistics).reduce((a, b) => a + b, 0)}
            </p>
          </div>
        </div>
      )}

      {/* Status Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => {
            handleStatusFilter(null);
          }}
          className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
            !statusFilter
              ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
          }`}
        >
          Toutes
        </button>
        {(
          [
            "draft",
            "pending",
            "sent",
            "partially_received",
            "received",
            "cancelled",
            "closed",
          ] as OrderStatus[]
        ).map((status) => (
          <button
            key={status}
            onClick={() => {
              handleStatusFilter(status);
            }}
            className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
              statusFilter === status
                ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
            }`}
          >
            {STATUS_LABELS[status]}
          </button>
        ))}
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : error ? (
        <div className="rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          Une erreur est survenue lors du chargement.
        </div>
      ) : orders && orders.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {orders.length} commande{orders.length > 1 ? "s" : ""}
          </p>
          <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
            {orders.map((order) => (
              <OrderRow key={order.id} order={order} formatCurrency={formatCurrency} />
            ))}
          </div>
        </div>
      ) : (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">Aucune commande trouvée</p>
        </div>
      )}
    </div>
  );
}

function OrderRow({
  order,
  formatCurrency,
}: {
  order: Order;
  formatCurrency: (cents: string | number) => string;
}) {
  return (
    <Link
      to={`/acquisition/orders/${order.id}`}
      className="block p-4 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <h3 className="font-medium text-gray-900 dark:text-white">{order.orderNumber}</h3>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[order.status]}`}
            >
              {STATUS_LABELS[order.status]}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <span>{order.supplierName}</span>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <span>
              {order.lineCount} ligne{order.lineCount > 1 ? "s" : ""}
            </span>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <span>{new Date(order.createdAt).toLocaleDateString("fr-FR")}</span>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm">
          <span className="font-medium text-gray-900 dark:text-white">
            {formatCurrency(order.totalInCents)}
          </span>
          <svg
            className="h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </Link>
  );
}

export default OrdersPage;
